import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Checkbox,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  CloudUpload as UploadIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteOutlineIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { MaterialChunksDialog } from '../components/MaterialChunksDialog';
import { PreprocessChip, isProcessing } from '../components/knowledge/PreprocessChip';
import { OrganizeDialog, NOTES_PLACEHOLDER } from '../components/knowledge/OrganizeDialog';
import { RawTextDialog } from '../components/knowledge/RawTextDialog';
import { DeleteCategoryDialog } from '../components/knowledge/DeleteCategoryDialog';
import {
  PreprocessConfig,
  PreprocessState,
  creditRange,
  estimateCredits,
  estimateTextChars,
  fetchPreprocessConfig,
  fmtCredits,
  preprocessDefaultOn,
} from '../services/knowledgeApi';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { learningPath, TargetType } from '../services/learningApi';
import {
  DataTable,
  PageHeader,
  DeleteConfirmationModal,
  type Column,
  type SortDirection,
} from '../components/common';

interface Material {
  id: string;
  name: string;
  description: string | null;
  file_name: string;
  file_size: number;
  file_type: string | null;
  created_at: string;
  uploaded_by: string;
  category_id?: string | null;
  vectorize_enabled?: boolean;
  vectorized_at?: string | null;
  material_metadata?: {
    source?: string;
    target_type?: TargetType;
    target_id?: string;
    target_name?: string;
    exported_at?: string;
    exported_from?: { target_type?: TargetType; target_id?: string; target_name?: string };
    preprocess?: PreprocessState;
  } | null;
}

const isManaged = (m: Material) => m.material_metadata?.source === 'self_improvement';
const isExport = (m: Material) => m.material_metadata?.source === 'self_improvement_export';

interface Category { id: string; name: string; }

// Safeguard constants
const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GB - allows large documents for vectorization
const MAX_MATERIALS_COUNT = 100; // Maximum number of materials per customer
const MAX_FILE_SIZE_DISPLAY = '1 GB';

const MyMaterials: React.FC = () => {
  const { getIdToken } = useAuth();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '';
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editMaterial, setEditMaterial] = useState<Material | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchFile, setSearchFile] = useState<File | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategory ? [initialCategory] : []);
  const [searchTopK, setSearchTopK] = useState<number>(3);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  
  // Category filtering for table
  const [filterCategoryId, setFilterCategoryId] = useState<string>(initialCategory);
  const [categoryStats, setCategoryStats] = useState<Record<string, number>>({});
  
  // Category management state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Delete confirmation (modal instead of window.confirm so Cancel actually cancels)
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [viewMaterial, setViewMaterial] = useState<Material | null>(null);
  const [organizeMaterial, setOrganizeMaterial] = useState<Material | null>(null);
  const [rawMaterial, setRawMaterial] = useState<Material | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [preprocessConfig, setPreprocessConfig] = useState<PreprocessConfig | null>(null);
  const [bulkNotes, setBulkNotes] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    file: null as File | null
  });
  
  // Multi-file upload state
  const [selectedFiles, setSelectedFiles] = useState<Array<{
    file: File;
    name: string;
    description: string;
    preprocess: boolean;
  }>>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const [vectorizeOnUpload, setVectorizeOnUpload] = useState(false);
  
  // Function to cleanse filename for bucket path safety
  const cleanseFileName = (filename: string): string => {
    // Remove file extension for the name field
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.')) || filename;
    
    // Replace spaces with hyphens, remove special characters except hyphens and underscores
    return nameWithoutExt
      .replace(/[^a-zA-Z0-9-_\s]/g, '') // Remove special chars except hyphens, underscores, spaces
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      .toLowerCase();
  };

  const loadCategoryStats = async () => {
    try {
      const token = await getIdToken();
      // Use dedicated endpoint for category statistics
      const response = await fetch(`/api/v2/materials/category-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('Failed to load category stats');
        return;
      }

      const data = await response.json();
      const stats = data.data || {};
      
      setCategoryStats(stats);
    } catch (error) {
      console.error('Error loading category stats:', error);
    }
  };

  const loadMaterials = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = await getIdToken();
      let url = `/api/v2/materials/?page=${page + 1}&page_size=${rowsPerPage}&sort_by=${sortBy}&sort_dir=${sortDir}`;
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      if (filterCategoryId) {
        url += `&category_id=${filterCategoryId}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load materials');

      const data = await response.json();
      const loadedMaterials = data.results || [];
      setMaterials(loadedMaterials);
      setTotalCount(data.pagination?.total_items || 0);
    } catch (error) {
      if (!silent) showSnackbar('Failed to load materials', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`/api/v2/customers/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const current = await response.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      // Load all categories with high limit
      const catsResp = await fetch(`/api/v2/customers/${customerId}/categories?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!catsResp.ok) return;
      const cats = await catsResp.json();
      setCategories(cats.data?.items || []);
    } catch (e) { /* ignore */ }
  };

  const submitVectorSearch = async () => {
    try {
      setSearchLoading(true);
      const token = await getIdToken();
      const currentResp = await fetch(`/api/v2/customers/current`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!currentResp.ok) throw new Error('Failed to resolve customer');
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) throw new Error('No customer');

      let resp;
      if (searchFile) {
        const fd = new FormData();
        fd.append('file', searchFile);
        resp = await fetch(`/api/v2/customers/${customerId}/vectors/search-file?top_k=${searchTopK}` + (selectedCategories.length ? selectedCategories.map(id => `&category_ids=${id}`).join('') : ''), {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
      } else {
        resp = await fetch(`/api/v2/customers/${customerId}/vectors/search`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            query: searchText,
            top_k: searchTopK,
            category_ids: selectedCategories.length > 0 ? selectedCategories : null
          })
        });
      }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Search failed');
      setSearchResults(data.data?.matches || []);
    } catch (e:any) {
      showSnackbar(e.message || 'Search failed', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy, sortDir, filterCategoryId]);

  useEffect(() => {
    loadCategories();
    loadCategoryStats();
    fetchPreprocessConfig(getIdToken).then(setPreprocessConfig).catch(() => setPreprocessConfig(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const anyProcessing = materials.some((m) => isProcessing(m.material_metadata?.preprocess));
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setTimeout(() => loadMaterials(true), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyProcessing, materials]);

  const handleSearch = () => {
    setPage(0);
    loadMaterials();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const columns: Column<Material>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileIcon color="action" />
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {value}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {row.file_name}
            </Typography>
            {isManaged(row) && (
              <Box sx={{ mt: 0.5 }}>
                <Tooltip title="Built and kept up to date by self-improvement. It can't be edited, recategorized or deleted here; manage its entries from the Learning page.">
                  {row.material_metadata?.target_type && row.material_metadata?.target_id ? (
                    <Chip
                      size="small"
                      color="secondary"
                      variant="outlined"
                      clickable
                      component={RouterLink}
                      to={learningPath(row.material_metadata.target_type, row.material_metadata.target_id, 'knowledge')}
                      label="Managed by self-improvement · Open"
                    />
                  ) : (
                    <Chip size="small" color="secondary" variant="outlined" label="Managed by self-improvement" />
                  )}
                </Tooltip>
              </Box>
            )}
            {isExport(row) && (
              <Box sx={{ mt: 0.5 }}>
                <Tooltip
                  title={`Frozen copy exported from self-improvement${
                    row.material_metadata?.exported_from?.target_name ? ` (${row.material_metadata.exported_from.target_name})` : ''
                  }${row.material_metadata?.exported_at ? ` on ${new Date(row.material_metadata.exported_at).toLocaleDateString()}` : ''}. It doesn't update as learning continues. It's yours to edit, move or delete, and runs only use it where its category is attached. Click to open the source.`}
                >
                  {row.material_metadata?.exported_from?.target_type && row.material_metadata?.exported_from?.target_id ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      clickable
                      component={RouterLink}
                      to={learningPath(row.material_metadata.exported_from.target_type, row.material_metadata.exported_from.target_id, 'knowledge')}
                      label="Frozen copy from self-improvement"
                    />
                  ) : (
                    <Chip size="small" variant="outlined" label="Frozen copy from self-improvement" />
                  )}
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      sortable: false,
      render: (value) => (
        <Typography variant="body2" color="textSecondary">
          {value || 'No description'}
        </Typography>
      ),
    },
    {
      id: 'file_size',
      label: 'Size',
      sortable: true,
      render: (value) => formatFileSize(value),
    },
    {
      id: 'file_type',
      label: 'Type',
      sortable: false,
      render: (value) => value ? (
        <Chip label={value} size="small" variant="outlined" />
      ) : '-',
    },
    {
      id: 'vector',
      label: 'Vectorize',
      sortable: false,
      render: (_, row) => isManaged(row) ? (
        <Tooltip title="Only entries marked “In use” on the Learning page are searchable; they're published automatically.">
          <Typography variant="caption" color="textSecondary">
            Managed{row.vectorized_at ? ` · ${new Date(row.vectorized_at).toLocaleDateString()}` : ''}
          </Typography>
        </Tooltip>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={!row.category_id ? "Please assign a category first" : ""}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleToggleVectorize(row)}
                  disabled={!row.category_id}
                >
                  {row.vectorize_enabled ? <ToggleOnIcon color="success" /> : <ToggleOffIcon color="disabled" />}
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" color="textSecondary">
              {!row.category_id ? 'Needs category' :
               row.vectorized_at ? `Last: ${new Date(row.vectorized_at).toLocaleDateString()}` :
               isProcessing(row.material_metadata?.preprocess) ? 'Processing…' : 'Not yet'}
            </Typography>
          </Box>
          <PreprocessChip
            state={row.material_metadata?.preprocess}
            vectorized={!!row.vectorized_at}
            onClick={() => setOrganizeMaterial(row)}
          />
        </Box>
      ),
    },
    {
      id: 'category_id',
      label: 'Category',
      sortable: false,
      render: (_, row) => (
        <TextField
          select
          SelectProps={{ native: true }}
          size="small"
          value={row.category_id || ''}
          disabled={isManaged(row)}
          onChange={(e) => handleSetCategory(row, e.target.value || null)}
        >
          <option value="">None</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </TextField>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title={row.vectorized_at ? 'View contents: the searchable snippets stored for this file' : 'Not vectorized yet, so there are no snippets to view'}>
            <span>
              <IconButton size="small" onClick={() => setViewMaterial(row)} disabled={!row.vectorized_at}>
                <ViewIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton
              size="small"
              onClick={() => handleDownload(row)}
              color="primary"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {!isManaged(row) && (
            <>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(row)}
                  color="default"
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const m = materials.find((mat) => mat.id === row.id);
                    if (m) setMaterialToDelete(m);
                  }}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const handleToggleVectorize = async (material: Material) => {
    if (!material.category_id && !material.vectorize_enabled) {
      showSnackbar('Please assign a category first', 'error');
      return;
    }
    
    // If disabling, show confirmation dialog
    if (material.vectorize_enabled) {
      const confirmed = window.confirm('Are you sure you want to disable vectorization? This will delete all embeddings and make this material unsearchable.');
      if (!confirmed) return;
    }
    
    try {
      const token = await getIdToken();
      const currentResp = await fetch(`/api/v2/customers/current`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!currentResp.ok) return;
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      const resp = await fetch(`/api/v2/customers/${customerId}/materials/${material.id}/vectorize`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enable: !material.vectorize_enabled })
      });
      if (!resp.ok) throw new Error('Failed to toggle vectorization');
      
      const result = await resp.json();
      if (material.vectorize_enabled) {
        showSnackbar(`Vectorization disabled. ${result.data?.embeddings_deleted || 0} embeddings deleted.`, 'info');
      } else {
        showSnackbar('Vectorization queued', 'success');
      }
      loadMaterials();
    } catch (e) {
      showSnackbar('Failed to queue vectorization', 'error');
    }
  };

  const handleSetCategory = async (material: Material, categoryId: string | null) => {
    try {
      const token = await getIdToken();
      const currentResp = await fetch(`/api/v2/customers/current`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!currentResp.ok) return;
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      const resp = await fetch(`/api/v2/customers/${customerId}/materials/${material.id}/set-category`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ category_id: categoryId })
      });
      if (!resp.ok) throw new Error('Failed to set category');
      showSnackbar('Category updated', 'success');
      loadMaterials();
      loadCategoryStats(); // Reload category counts
    } catch (e) {
      showSnackbar('Failed to update category', 'error');
    }
  };
  
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showSnackbar('Category name cannot be empty', 'error');
      return;
    }
    
    try {
      const token = await getIdToken();
      const currentResp = await fetch(`/api/v2/customers/current`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!currentResp.ok) return;
      const current = await currentResp.json();
      const customerId = current?.data?.customer_id;
      if (!customerId) return;
      
      const resp = await fetch(`/api/v2/customers/${customerId}/categories`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.detail || 'Failed to create category');
      }
      
      showSnackbar('Category created successfully', 'success');
      setNewCategoryName('');
      setCategoryDialogOpen(false);
      loadCategories();
    } catch (e: any) {
      showSnackbar(e.message || 'Failed to create category', 'error');
    }
  };


  const handleMultiFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    const fileArray = Array.from(files);
    const oversizedFiles = fileArray.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    
    if (oversizedFiles.length > 0) {
      showSnackbar(`${oversizedFiles.length} file(s) exceed the ${MAX_FILE_SIZE_DISPLAY} limit and were skipped`, 'warning');
    }
    
    const validFiles = fileArray
      .filter(file => file.size <= MAX_FILE_SIZE_BYTES)
      .map(file => ({
        file,
        name: cleanseFileName(file.name),
        description: '',
        preprocess: preprocessDefaultOn(file, preprocessConfig),
      }));
    
    setSelectedFiles(validFiles);
  };

  const handleBatchUpload = async () => {
    if (selectedFiles.length === 0) {
      showSnackbar('No files selected', 'error');
      return;
    }

    // Check if adding these files would exceed the maximum materials count
    if (totalCount + selectedFiles.length > MAX_MATERIALS_COUNT) {
      showSnackbar(
        `Cannot upload: You have ${totalCount} materials and would exceed the maximum of ${MAX_MATERIALS_COUNT}. Please delete some materials first.`,
        'error'
      );
      return;
    }

    setUploadingFiles(true);
    const token = await getIdToken();
    let successCount = 0;
    let failedCount = 0;
    const failures: string[] = [];
    const renamed: string[] = [];
    const uploadedMaterials: Array<{id: string, name: string}> = [];

    // Get customer ID for vectorization
    let customerId: string | null = null;
    if (vectorizeOnUpload && bulkCategoryId) {
      try {
        const currentResp = await fetch(`/api/v2/customers/current`, { 
          headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (currentResp.ok) {
          const current = await currentResp.json();
          customerId = current?.data?.customer_id;
        }
      } catch (e) {

      }
    }

    for (const fileData of selectedFiles) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append('name', fileData.name);
        formDataToSend.append('description', fileData.description || '');
        formDataToSend.append('file', fileData.file);
        formDataToSend.append('on_conflict', 'rename');
        if (bulkCategoryId) {
          formDataToSend.append('category_id', bulkCategoryId);
        }
        if (preprocessConfig) {
          const organize = preprocessConfig.enabled && vectorizeOnUpload && fileData.preprocess;
          formDataToSend.append('preprocess', String(organize));
          if (organize && bulkNotes.trim()) formDataToSend.append('preprocess_notes', bulkNotes.trim());
        }

        const response = await fetch('/api/v2/materials/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });

        if (response.ok) {
          successCount++;
          const data = await response.json();
          if (data?.data?.id) {
            uploadedMaterials.push({ id: data.data.id, name: data.data.name || fileData.name });
            if (data.data.name && data.data.name !== fileData.name) renamed.push(`“${fileData.name}” → “${data.data.name}”`);
          }
        } else {
          failedCount++;
          const data = await response.json().catch(() => null);
          failures.push(`${fileData.name}: ${data?.detail || data?.message || `error ${response.status}`}`);
        }
      } catch (error: any) {
        failedCount++;
        failures.push(`${fileData.name}: ${error?.message || 'upload failed'}`);
      }
    }

    // Vectorize uploaded materials if requested
    if (vectorizeOnUpload && bulkCategoryId && customerId && uploadedMaterials.length > 0) {
      showSnackbar('Starting vectorization...', 'info');
      
      let vectorizeSuccess = 0;
      for (const material of uploadedMaterials) {
        try {
          const resp = await fetch(`/api/v2/customers/${customerId}/materials/${material.id}/vectorize`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ enable: true })
          });
          
          if (resp.ok) {
            vectorizeSuccess++;
          } else {

          }
        } catch (e) {

        }
      }
      
      if (vectorizeSuccess > 0) {
        showSnackbar(`${vectorizeSuccess} material(s) queued for vectorization`, 'success');
      }
    }

    setUploadingFiles(false);
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setBulkCategoryId('');
    setVectorizeOnUpload(false);
    setBulkNotes('');
    
    const notes = [
      renamed.length ? `Name already used, saved as: ${renamed.join(', ')}.` : '',
      failures.length ? `Failed — ${failures.join('; ')}` : '',
    ].filter(Boolean).join(' ');
    if (successCount > 0) {
      showSnackbar(`Uploaded ${successCount} file(s)${failedCount > 0 ? `, ${failedCount} failed` : ''}. ${notes}`.trim(), failedCount > 0 ? 'warning' : 'success');
      loadMaterials();
      loadCategoryStats(); // Reload category counts
    } else {
      showSnackbar(notes || 'Failed to upload files', 'error');
    }
  };

  const handleDownload = async (material: Material) => {
    try {
      const token = await getIdToken();
      const response = await fetch(`/api/v2/materials/${material.id}/download-url`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to get download URL');

      const data = await response.json();
      const link = document.createElement('a');
      link.href = data.data.download_url;
      link.download = data.data.file_name || material.file_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {

      showSnackbar('Failed to get download URL', 'error');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!materialToDelete) return;

    const materialId = materialToDelete.id;
    const token = await getIdToken();
    const response = await fetch(`/api/v2/materials/${materialId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error('Failed to delete material');

    showSnackbar('Material deleted successfully', 'success');
    loadMaterials();
    loadCategoryStats();
  };

  const handleEditClick = (material: Material) => {
    setEditMaterial(material);
    setFormData({
      name: material.name,
      description: material.description || '',
      file: null
    });
    setUploadDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editMaterial) return;

    try {
      setUploadLoading(true);
      const token = await getIdToken();
      
      const response = await fetch(`/api/v2/materials/${editMaterial.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null
        })
      });

      if (!response.ok) throw new Error('Failed to update material');

      showSnackbar('Material updated successfully', 'success');
      setUploadDialogOpen(false);
      setEditMaterial(null);
      setFormData({ name: '', description: '', file: null });
      loadMaterials();
    } catch (error) {

      showSnackbar('Failed to update material', 'error');
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Box>
      {/* Vector Search Panel */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Enter query text"
              />
              <Box sx={{ mt: 1 }}>
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="vector-search-file"
                  type="file"
                  onChange={(e) => setSearchFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="vector-search-file">
                  <Button variant="outlined" component="span">
                    {searchFile ? `File: ${searchFile.name}` : 'Attach file (optional)'}
                  </Button>
                </label>
                {searchFile && (
                  <Button sx={{ ml: 1 }} onClick={() => setSearchFile(null)}>Clear file</Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="category-select-label">Categories</InputLabel>
                <Select
                  labelId="category-select-label"
                  multiple
                  value={selectedCategories}
                  label="Categories"
                  renderValue={(selected) => selected.map(id => categories.find(c => c.id === id)?.name || id).join(', ')}
                  onChange={(e) => setSelectedCategories(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]))}
                >
                  {categories.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={selectedCategories.indexOf(c.id) > -1} />
                      <Typography>{c.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                sx={{ mt: 2 }}
                label="Results"
                type="number"
                inputProps={{ min: 1, max: 100 }}
                value={searchTopK}
                onChange={(e) => setSearchTopK(Math.min(100, Math.max(1, parseInt(e.target.value || '3', 10))))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="contained" onClick={submitVectorSearch} disabled={searchLoading} fullWidth sx={{ height: '56px' }}>
                {searchLoading ? <CircularProgress size={24} /> : 'Search vectors'}
              </Button>
            </Grid>
          </Grid>
          {/* Results */}
          {searchResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Search Results</Typography>
              <Grid container spacing={2}>
                {searchResults.map((result, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Material ID: {result.material_id.slice(0, 8)}...
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            overflow: 'auto',
                            flexGrow: 1,
                            maxHeight: 200,
                            bgcolor: 'grey.50',
                            p: 1,
                            borderRadius: 1,
                            fontSize: '0.875rem'
                          }}
                        >
                          {result.content}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Category Management */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                📁 Categories
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Organize your knowledge base materials
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              onClick={() => setCategoryDialogOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              New Category
            </Button>
          </Box>
          {categories.length === 0 ? (
            <Alert severity="info">
              No categories yet. Create one to start organizing your materials and enable knowledge search.
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
              <Grid container spacing={2}>
                {categories.map((cat) => {
                const count = categoryStats[cat.id] || 0;
                const color = ['#1976d2', '#9c27b0', '#f57c00', '#388e3c', '#d32f2f', '#0288d1', '#7b1fa2', '#c2185b'][
                  categories.indexOf(cat) % 8
                ];
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={cat.id}>
                    <Card 
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        borderColor: filterCategoryId === cat.id ? color : 'grey.300',
                        borderWidth: filterCategoryId === cat.id ? 2 : 1,
                        bgcolor: filterCategoryId === cat.id ? `${color}10` : 'white',
                        '&:hover': {
                          borderColor: color,
                          boxShadow: 2,
                          transform: 'translateY(-2px)',
                        }
                      }}
                      onClick={() => {
                        setFilterCategoryId(filterCategoryId === cat.id ? '' : cat.id);
                        setPage(0);
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 600,
                              color: color,
                              wordBreak: 'break-word',
                              flex: 1
                            }}
                          >
                            {cat.name}
                          </Typography>
                          <Chip
                            label={count}
                            size="small"
                            sx={{
                              bgcolor: `${color}20`,
                              color: color,
                              fontWeight: 600,
                              ml: 1
                            }}
                          />
                          <Tooltip title="Delete category">
                            <IconButton
                              size="small"
                              aria-label={`Delete category ${cat.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCategoryToDelete(cat);
                              }}
                              sx={{ ml: 0.5, mt: -0.5, mr: -1, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {count} {count === 1 ? 'file' : 'files'}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Tooltip title="Click to copy category ID">
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: 'monospace',
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(cat.id);
                                showSnackbar('Category ID copied', 'info');
                              }}
                            >
                              {cat.id.slice(0, 8)}...
                            </Typography>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              </Grid>
            </Box>
          )}
          {filterCategoryId && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`Filtered by: ${categories.find(c => c.id === filterCategoryId)?.name}`}
                onDelete={() => {
                  setFilterCategoryId('');
                  setPage(0);
                }}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Box>
        <PageHeader
          title="Knowledge Base"
          subtitle="Create categories and upload documents for AI functions to reference"
          actions={[
            {
              label: 'Add Materials',
              onClick: () => {
                if (categories.length === 0) {
                  showSnackbar('Please create at least one category before uploading materials.', 'warning');
                  return;
                }
                if (totalCount >= MAX_MATERIALS_COUNT) {
                  showSnackbar(`Maximum materials limit (${MAX_MATERIALS_COUNT}) reached. Please delete some materials first.`, 'error');
                  return;
                }
                setEditMaterial(null);
                setSelectedFiles([]);
                setBulkCategoryId(
                  categories.some((c) => c.id === filterCategoryId) ? filterCategoryId : categories.length === 1 ? categories[0].id : '',
                );
                setBulkNotes('');
                setVectorizeOnUpload(true); // Default to checked
                setUploadDialogOpen(true);
              },
              icon: <AddIcon />,
              variant: 'contained',
              disabled: totalCount >= MAX_MATERIALS_COUNT || categories.length === 0,
              tooltip: categories.length === 0 ? 'Create at least one category before uploading materials' : undefined,
            },
          ]}
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          onSearch={handleSearch}
          searchPlaceholder="Search materials..."
          showSearch
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: -1 }}>
          <Chip 
            label={`${totalCount} / ${MAX_MATERIALS_COUNT} materials`} 
            size="small" 
            color={totalCount >= MAX_MATERIALS_COUNT ? 'error' : totalCount >= MAX_MATERIALS_COUNT * 0.8 ? 'warning' : 'default'}
            variant="outlined"
          />
          <Chip 
            label={`Max file size: ${MAX_FILE_SIZE_DISPLAY}`} 
            size="small" 
            variant="outlined"
          />
        </Box>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <DataTable
            columns={columns}
            data={materials}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sortBy={sortBy}
            sortDirection={sortDir}
            onSort={handleSort}
            rowKey="id"
            emptyMessage={searchTerm ? 'No materials found matching your search.' : 'No materials uploaded yet. Click "Upload Material" to get started.'}
          />
        </CardContent>
      </Card>

      {/* Upload/Edit Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMaterial ? 'Edit Material' : 'Add Materials'}</DialogTitle>
        <DialogContent>
          {editMaterial ? (
            // Edit mode - single material
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Material Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Description (Optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <Alert severity="info" sx={{ mb: 2 }}>
                You can only update the name and description. To change the file, please delete this material and upload a new one.
              </Alert>
            </Box>
          ) : (
            // Multi-file upload mode
            <Box sx={{ pt: 2 }}>
              {selectedFiles.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <input
                    accept="*/*"
                    style={{ display: 'none' }}
                    id="multi-file-upload"
                    type="file"
                    multiple
                    onChange={(e) => handleMultiFileSelect(e.target.files)}
                  />
                  <label htmlFor="multi-file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadIcon />}
                      size="large"
                    >
                      Select Files
                    </Button>
                  </label>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Click to select multiple files (Max {MAX_FILE_SIZE_DISPLAY} each)
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                    You can upload up to {MAX_MATERIALS_COUNT - totalCount} more material(s)
                  </Alert>
                </Box>
              ) : (
                <Box>
                  {totalCount + selectedFiles.length > MAX_MATERIALS_COUNT && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      Cannot upload: This would exceed the maximum of {MAX_MATERIALS_COUNT} materials. 
                      Please remove {totalCount + selectedFiles.length - MAX_MATERIALS_COUNT} file(s) from this upload.
                    </Alert>
                  )}
                  {totalCount + selectedFiles.length > MAX_MATERIALS_COUNT * 0.8 && totalCount + selectedFiles.length <= MAX_MATERIALS_COUNT && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      You're approaching the materials limit. After this upload, you'll have {totalCount + selectedFiles.length} of {MAX_MATERIALS_COUNT} materials.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{selectedFiles.length} file(s) selected</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const input = document.getElementById('multi-file-upload-add') as HTMLInputElement;
                        input?.click();
                      }}
                      disabled={totalCount + selectedFiles.length >= MAX_MATERIALS_COUNT}
                    >
                      Add More Files
                    </Button>
                    <input
                      accept="*/*"
                      style={{ display: 'none' }}
                      id="multi-file-upload-add"
                      type="file"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          handleMultiFileSelect(e.target.files);
                        }
                      }}
                    />
                  </Box>
                  
                  {/* Bulk Settings */}
                  <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: !bulkCategoryId ? '#fff3e0' : 'grey.50', border: !bulkCategoryId ? '2px solid #ff9800' : undefined }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Bulk Settings (applies to all files) {!bulkCategoryId && <Typography component="span" color="warning.main">*Required</Typography>}
                    </Typography>
                    {!bulkCategoryId && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        You must select a category before uploading materials.
                      </Alert>
                    )}
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small" required error={!bulkCategoryId}>
                          <InputLabel>Category *</InputLabel>
                          <Select
                            value={bulkCategoryId}
                            onChange={(e) => setBulkCategoryId(e.target.value)}
                            label="Category *"
                          >
                            <MenuItem value="" disabled>
                              <em>Select a category</em>
                            </MenuItem>
                            {categories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Tooltip title={!bulkCategoryId ? "Select a category to enable vectorization" : ""}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={vectorizeOnUpload}
                                onChange={(e) => setVectorizeOnUpload(e.target.checked)}
                                disabled={!bulkCategoryId}
                              />
                            }
                            label="Vectorize on upload"
                          />
                        </Tooltip>
                      </Grid>
                    </Grid>
                    {preprocessConfig?.enabled && vectorizeOnUpload && (() => {
                      const organized = selectedFiles.filter((f) => f.preprocess);
                      const [total, totalMax] = organized.reduce(
                        ([lo, hi], f) => {
                          const [a, b] = creditRange(estimateTextChars(f.file), preprocessConfig);
                          return [lo + a, hi + b];
                        },
                        [0, 0],
                      );
                      return (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <AutoAwesomeIcon fontSize="small" color="secondary" />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              AI organizing: {organized.length} of {selectedFiles.length} file{selectedFiles.length === 1 ? '' : 's'}
                            </Typography>
                            <Button size="small" onClick={() => setSelectedFiles(selectedFiles.map((f) => ({ ...f, preprocess: true })))}>
                              All
                            </Button>
                            <Button size="small" onClick={() => setSelectedFiles(selectedFiles.map((f) => ({ ...f, preprocess: false })))}>
                              None
                            </Button>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: organized.length ? 1.5 : 0 }}>
                            The AI rewrites each ticked file into self-contained, titled snippets with page references, so your
                            functions and chains find the right part and get answers that make sense on their own. It keeps every fact
                            and adds nothing. Uses your AI credits
                            {organized.length
                              ? `: roughly ${fmtCredits(total)} credits for this upload, up to ${fmtCredits(totalMax)} if parts need a second try (rough estimate; never more than ${preprocessConfig.max_credits_per_file} per file).`
                              : '.'}
                          </Typography>
                          {organized.length > 0 && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Notes for the AI (optional, applies to every ticked file)"
                              multiline
                              minRows={2}
                              value={bulkNotes}
                              onChange={(e) => setBulkNotes(e.target.value.slice(0, preprocessConfig.max_notes_chars))}
                              placeholder={NOTES_PLACEHOLDER}
                              helperText={`What matters, who it's for, how to group things, terms to keep. It can't add facts or remove content. ${bulkNotes.length}/${preprocessConfig.max_notes_chars}`}
                            />
                          )}
                        </Box>
                      );
                    })()}
                  </Card>
                  
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {selectedFiles.map((fileData, index) => (
                      <Card key={index} sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {fileData.file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {formatFileSize(fileData.file.size)} • {fileData.file.type || 'Unknown type'}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <TextField
                          fullWidth
                          label="Material Name"
                          value={fileData.name}
                          onChange={(e) => {
                            const updated = [...selectedFiles];
                            updated[index].name = e.target.value;
                            setSelectedFiles(updated);
                          }}
                          sx={{ mb: 1 }}
                          size="small"
                          required
                        />
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          value={fileData.description}
                          onChange={(e) => {
                            const updated = [...selectedFiles];
                            updated[index].description = e.target.value;
                            setSelectedFiles(updated);
                          }}
                          size="small"
                          multiline
                          rows={2}
                        />
                        {preprocessConfig?.enabled && vectorizeOnUpload && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={fileData.preprocess}
                                  onChange={(e) => {
                                    const updated = [...selectedFiles];
                                    updated[index] = { ...updated[index], preprocess: e.target.checked };
                                    setSelectedFiles(updated);
                                  }}
                                />
                              }
                              label={<Typography variant="body2">AI-organize</Typography>}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {fileData.preprocess
                                ? (() => {
                                    const c = estimateCredits(fileData.file, preprocessConfig);
                                    return `≈ ${fmtCredits(c)} credits`;
                                  })()
                                : preprocessConfig.default_off_extensions.some((ext) => fileData.file.name.toLowerCase().endsWith(ext))
                                  ? 'Off by default: data files are already split one row at a time'
                                  : fileData.file.size > preprocessConfig.default_on_max_bytes
                                    ? 'Off by default for very large files'
                                    : 'Standard split by headings, pages and rows'}
                            </Typography>
                          </Box>
                        )}
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFiles([]);
            setBulkCategoryId('');
            setVectorizeOnUpload(false);
            setBulkNotes('');
          }}>
            Cancel
          </Button>
          {editMaterial ? (
            <Button
              onClick={handleUpdate}
              variant="contained"
              disabled={uploadLoading || !formData.name}
            >
              {uploadLoading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          ) : (
            <Tooltip title={!bulkCategoryId ? "Please select a category to upload to" : ""}>
              <span>
                <Button
                  onClick={handleBatchUpload}
                  variant="contained"
                  disabled={
                    uploadingFiles || 
                    selectedFiles.length === 0 || 
                    selectedFiles.some(f => !f.name) ||
                    totalCount + selectedFiles.length > MAX_MATERIALS_COUNT ||
                    !bulkCategoryId
                  }
                  startIcon={uploadingFiles ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {uploadingFiles ? `Uploading...` : `Upload ${selectedFiles.length} File(s)`}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>

      {/* Category Creation Dialog */}
      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateCategory();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCategoryDialogOpen(false);
            setNewCategoryName('');
          }}>
            Cancel
          </Button>
          <Button onClick={handleCreateCategory} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {materialToDelete && (
        <DeleteConfirmationModal
          open={!!materialToDelete}
          onClose={() => setMaterialToDelete(null)}
          onConfirm={handleDeleteConfirmed}
          title="Delete Material"
          itemName={materialToDelete.name}
          itemType="material"
          message={`Are you sure you want to permanently delete "${materialToDelete.name}"? This will remove the file and all associated embeddings.`}
          isDeactivation={false}
        />
      )}

      {viewMaterial && (
        <MaterialChunksDialog
          materialId={viewMaterial.id}
          title={viewMaterial.name}
          intro={
            isManaged(viewMaterial)
              ? 'Written by self-improvement: one snippet per learning that is in use, with how many runs back it up. Waiting learnings are not here until approved. Runs get the snippets whose name appears in their input first, then the most similar ones.'
              : undefined
          }
          actions={
            isManaged(viewMaterial) && viewMaterial.material_metadata?.target_type && viewMaterial.material_metadata?.target_id ? (
              <Button
                component={RouterLink}
                to={learningPath(viewMaterial.material_metadata.target_type, viewMaterial.material_metadata.target_id, 'knowledge')}
              >
                Manage in Learning
              </Button>
            ) : isManaged(viewMaterial) ? undefined : (
              <>
                <Button onClick={() => setRawMaterial(viewMaterial)}>View original text</Button>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => {
                    setOrganizeMaterial(viewMaterial);
                    setViewMaterial(null);
                  }}
                >
                  AI organizing
                </Button>
              </>
            )
          }
          onClose={() => setViewMaterial(null)}
        />
      )}

      {organizeMaterial && (
        <OrganizeDialog
          material={organizeMaterial}
          state={organizeMaterial.material_metadata?.preprocess || {}}
          config={preprocessConfig}
          onClose={() => setOrganizeMaterial(null)}
          onViewOriginal={() => setRawMaterial(organizeMaterial)}
          onDone={(message) => {
            setOrganizeMaterial(null);
            showSnackbar(message, 'success');
            loadMaterials(true);
          }}
        />
      )}

      {rawMaterial && (
        <RawTextDialog materialId={rawMaterial.id} title={rawMaterial.name} onClose={() => setRawMaterial(null)} />
      )}

      {categoryToDelete && (
        <DeleteCategoryDialog
          category={categoryToDelete}
          onClose={() => setCategoryToDelete(null)}
          onDeleted={(message) => {
            const id = categoryToDelete.id;
            setCategoryToDelete(null);
            setSelectedCategories((prev) => prev.filter((c) => c !== id));
            if (bulkCategoryId === id) setBulkCategoryId('');
            showSnackbar(message, 'success');
            loadCategories();
            loadCategoryStats();
            if (filterCategoryId === id) {
              setFilterCategoryId('');
              setPage(0);
            } else {
              loadMaterials();
            }
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyMaterials;
